import { robotsMeta } from "@/lib/seo-quality";
import { assessModelYear } from "@/lib/seo-families/model-year";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Phone, ArrowRight, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { PageHeader } from "./fordon";
import { getModelYearByParams, MODEL_YEARS, type ModelYear } from "@/data/rapido-year-matrix";
import { getModelYearDjup } from "@/data/rapido-year-djup";

export const Route = createFileRoute("/rapido/modeller/$model_/$year")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const my = getModelYearByParams(params.model, params.year);
    if (!my) throw notFound();
    return { my, robots: robotsMeta(assessModelYear(my)) };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const m = loaderData.my;
    const djup = getModelYearDjup(m.slug);
    const url = `https://www.akhusbilfritid.se/rapido/modeller/${m.model.toLowerCase()}/${m.year}`;
    return {
      meta: [
        ...loaderData.robots,
        { title: m.metaTitle },
        { name: "description", content: m.metaDesc },
        { name: "keywords", content: `${m.focusKeyword}, ${m.modelDisplay.toLowerCase()} ${m.year}, ${m.modelDisplay.toLowerCase()} pris, ${m.modelDisplay.toLowerCase()} test, rapido husbil` },
        { property: "og:type", content: "article" },
        { property: "og:title", content: m.metaTitle },
        { property: "og:description", content: m.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: `${m.modelDisplay} ${m.year}`,
            brand: { "@type": "Brand", name: "Rapido" },
            category: m.category,
            description: m.intro,
            model: `${m.modelDisplay} ${m.year}`,
            productionDate: `${m.year}`,
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "SEK",
              lowPrice: m.usedPriceRange.replace(/\D/g, "").slice(0, 6) || "500000",
              availability: "https://schema.org/InStock",
              seller: { "@type": "AutoDealer", name: "AK Husbil & Fritid" },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [...m.faq, ...(djup?.faq ?? [])].map((f: { q: string; a: string }) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se" },
              { "@type": "ListItem", position: 2, name: "Rapido", item: "https://www.akhusbilfritid.se/rapido" },
              { "@type": "ListItem", position: 3, name: m.modelDisplay, item: `https://www.akhusbilfritid.se/rapido/modeller/${m.model.toLowerCase()}` },
              { "@type": "ListItem", position: 4, name: `${m.year}`, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="max-w-2xl mx-auto p-8 text-center">
      <h1 className="text-2xl font-bold mb-4">Denna modell/år finns inte</h1>
      <Link to="/rapido" className="text-primary underline">Till Rapido-portalen</Link>
    </div>
  ),
  component: ModelYearPage,
});

function ModelYearPage() {
  const data = Route.useLoaderData() as { my: ModelYear };
  const m = data.my;
  const djup = getModelYearDjup(m.slug);
  const otherYears = MODEL_YEARS.filter((x) => x.model === m.model && x.slug !== m.slug);

  return (
    <>
      <PageHeader label={m.category} title={`${m.modelDisplay} ${m.year}`} sub={`Test, kända problem och begagnatpris — ${m.usedPriceRange}`} />

      <main className="max-w-4xl mx-auto px-4 py-10 space-y-12">
        <nav className="text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-2">/</span>
          <Link to="/rapido" className="hover:underline">Rapido</Link>
          <span className="mx-2">/</span>
          <span>{m.modelDisplay} {m.year}</span>
        </nav>

        <section>
          <p className="text-lg leading-relaxed">{m.intro}</p>
        </section>

        {/* Fakta */}
        <section className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-lg border p-4">
            <div className="text-xs text-muted-foreground mb-1">Chassi</div>
            <div className="font-semibold">{m.chassi}</div>
          </div>
          <div className="rounded-lg border p-4">
            <div className="text-xs text-muted-foreground mb-1">Motor</div>
            <div className="font-semibold">{m.motor}</div>
          </div>
          <div className="rounded-lg border p-4 sm:col-span-2">
            <div className="text-xs text-muted-foreground mb-1">Realistisk begagnatpris 2026</div>
            <div className="font-semibold text-lg">{m.usedPriceRange}</div>
          </div>
        </section>

        {/* Ändringar detta år */}
        {m.yearChanges.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-4">Nyheter för {m.year}</h2>
            <ul className="space-y-2">
              {m.yearChanges.map((c, i) => (
                <li key={i} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Kända problem */}
        {m.knownIssues.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-4">Kända problem & åtgärder</h2>
            <div className="space-y-4">
              {m.knownIssues.map((k, i) => (
                <div key={i} className="border rounded-lg p-4">
                  <div className="flex gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-orange-500 shrink-0" />
                    <div className="font-semibold">{k.issue}</div>
                  </div>
                  <div className="text-sm text-muted-foreground pl-7"><strong>Åtgärd:</strong> {k.fix}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Att kolla vid köp */}
        <section>
          <h2 className="text-2xl font-bold mb-4">Vad du ska kontrollera vid köp</h2>
          <ul className="space-y-2">
            {m.whatToCheck.map((w, i) => (
              <li key={i} className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Djupinnehåll: marknadsläge, driftkostnad, körkort/vikt, vinterbruk, serviceläge, förhandling, ägarerfarenhet */}
        {djup && (
          <section className="space-y-6">
            <h2 className="text-2xl font-bold">Fördjupning: {m.modelDisplay} {m.year}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground mb-1">Marknadsläge</div>
                <p className="text-sm leading-relaxed">{djup.marknadslage}</p>
              </div>
              <div className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground mb-1">Driftkostnad</div>
                <p className="text-sm leading-relaxed">{djup.driftkostnad}</p>
              </div>
              <div className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground mb-1">Körkort & vikt</div>
                <p className="text-sm leading-relaxed">{djup.korkortOchVikt}</p>
              </div>
              <div className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground mb-1">Vinterbruk</div>
                <p className="text-sm leading-relaxed">{djup.vinterbruk}</p>
              </div>
              <div className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground mb-1">Serviceläge</div>
                <p className="text-sm leading-relaxed">{djup.servicelage}</p>
              </div>
              <div className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground mb-1">Förhandlingsläge</div>
                <p className="text-sm leading-relaxed">{djup.forhandling}</p>
              </div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-xs text-muted-foreground mb-1">Ägarerfarenhet</div>
              <p className="text-sm leading-relaxed">{djup.agarerfarenhet}</p>
            </div>
          </section>
        )}

        {/* Checklista vid köp */}
        {djup && djup.checklista.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-4">Checklista vid köp</h2>
            <ul className="space-y-2">
              {djup.checklista.map((c: string, i: number) => (
                <li key={i} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* FAQ */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Vanliga frågor</h2>
          <div className="space-y-4">
            {[...m.faq, ...(djup?.faq ?? [])].map((f: { q: string; a: string }, i: number) => (
              <div key={i} className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">{f.q}</h3>
                <p className="text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-xl bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Intresserad av {m.modelDisplay} {m.year}?</h2>
          <p className="mb-6 opacity-90">
            Vi är officiell Rapido-återförsäljare och känner varje årsmodell in i minsta detalj. Har vi en {m.modelDisplay} i lager just nu så ligger den på Fyndanu — annars ringer vi dig när nästa kommer in.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:0708629743" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background text-foreground font-semibold hover:opacity-90">
              <Phone className="w-4 h-4" /> 070-862 97 43
            </a>
            <a
              href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background/10 border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10"
            >
              Sök {m.modelDisplay} på Fyndanu
            </a>
            <Link to="/rapido/begagnade" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10">
              Alla begagnade Rapido
            </Link>
          </div>
        </section>

        {/* Andra årsmodeller */}
        {otherYears.length > 0 && (
          <section>
            <h2 className="text-xl font-bold mb-4">Andra årsmodeller av {m.modelDisplay}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {otherYears.map((y) => (
                <Link
                  key={y.slug}
                  to="/rapido/modeller/$model/$year"
                  params={{ model: y.model.toLowerCase(), year: String(y.year) }}
                  className="flex items-center justify-between px-4 py-3 rounded-lg border hover:bg-muted transition"
                >
                  <span>{y.modelDisplay} {y.year}</span>
                  <ArrowRight className="w-4 h-4 opacity-50" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="pt-6 border-t">
          <Link to="/rapido" className="inline-flex items-center gap-2 text-primary hover:underline">
            <ArrowLeft className="w-4 h-4" /> Tillbaka till Rapido-portalen
          </Link>
        </div>
      </main>
    </>
  );
}
